import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  reviewBoardApi,
  type ReviewBoardRow,
  type ReviewCategory,
  type ReviewOrderBy,
} from '../../api/reviewBoard.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { num } from '../../lib/format.js';

/**
 * 리뷰 관리 — 원본 zaiko/filter_review_list.php + filter_review_load_data.php
 * + filter_review_merge.php.
 *
 * 원본과 같은 구성·동작을 그대로 옮겼다.
 *   - 무한 스크롤: 표 아래에 닿으면 다음 50건을 이어붙인다(끝 신호는 빈
 *     페이지). 그래서 여기 페이지네이션 컴포넌트를 안 쓴다 - React Query 의
 *     useInfiniteQuery 로 "빈 페이지가 오면 멈춘다"만 구현한다.
 *   - 같은 페이지 안에서 주문번호(marketIdx)가 겹치는 행은 배경색으로 묶어
 *     보여준다(원본의 bg-dup-color-N, 5색 순환) - 서버가 판단한 값을 그대로
 *     쓴다(reviewBoard.service.ts).
 *   - 체크박스로 2개 이상 고르면 '선택 병합' - 가장 오래된(번호가 작은)
 *     리뷰에 나머지 내용을 이어붙인다.
 *   - 제목·작성자·이미지·주문번호는 전부 원본처럼 고정 크기 팝업 창으로 연다
 *     (레거시 관리 화면과 같은 창에서 계속 작업할 수 있게).
 *
 * 원본의 session 접근코드(888544) 게이트는 안 옮겼다 - 다른 콘텐츠기획
 * 화면들과 같은 등급(PRODUCT_ROLES)으로 이미 막혀 있다(reports/index.ts).
 */

const CATEGORIES: { value: ReviewCategory; label: string }[] = [
  { value: 'photoafter', label: '포토후기' },
  { value: 'textafter', label: '텍스트후기' },
];

function thisMonthISO(): string {
  return new Date().toISOString().slice(0, 7);
}
function monthsAgoISO(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 7);
}

interface Filters {
  search: string;
  category: ReviewCategory;
  start: string;
  end: string;
  orderby: ReviewOrderBy;
  order: 'ASC' | 'DESC';
}

function defaultFilters(): Filters {
  // 원본 :55-60 - 검색 기간 기본값은 5개월 전 ~ 이번 달, 리뷰 선택 기본은 포토후기.
  return {
    search: '',
    category: 'photoafter',
    start: monthsAgoISO(5),
    end: thisMonthISO(),
    orderby: 'index_no',
    order: 'DESC',
  };
}

export function ReviewBoardPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const committed = useMemo<Filters>(() => {
    const d = defaultFilters();
    const category = params.get('category');
    const order = params.get('order');
    return {
      search: params.get('search') ?? d.search,
      category: category === 'textafter' ? 'textafter' : d.category,
      start: params.get('start') ?? d.start,
      end: params.get('end') ?? d.end,
      orderby: (params.get('orderby') as ReviewOrderBy | null) ?? d.orderby,
      order: order === 'ASC' ? 'ASC' : d.order,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // 검색어/기간은 [검색] 을 눌러야 반영된다(원본도 폼 submit 이었다). 리뷰
  // 선택(카테고리)만 원본처럼 바꾸는 즉시 다시 조회한다.
  const [draft, setDraft] = useState({ search: committed.search, start: committed.start, end: committed.end });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeOk, setMergeOk] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [merging, setMerging] = useState(false);

  const queryKey = ['review-board', committed] as const;

  const list = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => reviewBoardApi.list({ ...committed, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (last.items.length > 0 ? pages.length + 1 : undefined),
  });

  const rows: ReviewBoardRow[] = list.data?.pages.flatMap((p) => p.items) ?? [];

  function applyFilters(next: Partial<Filters> = {}) {
    const merged = { ...committed, ...next };
    setParams(
      {
        search: draft.search,
        category: merged.category,
        start: draft.start,
        end: draft.end,
        orderby: merged.orderby,
        order: merged.order,
      },
      { replace: true },
    );
    setSelected(new Set());
    setMergeOk(null);
    setMergeError(null);
  }

  function resetAll() {
    const d = defaultFilters();
    setDraft({ search: d.search, start: d.start, end: d.end });
    setParams({}, { replace: true });
    setSelected(new Set());
    setMergeOk(null);
    setMergeError(null);
  }

  /** 원본 sortTable - 같은 컬럼이면 방향만 뒤집고, 다른 컬럼이면 그 컬럼으로 내림차순부터. */
  function sortBy(column: ReviewOrderBy) {
    const nextOrder = committed.orderby === column && committed.order === 'DESC' ? 'ASC' : 'DESC';
    applyFilters({ orderby: column, order: nextOrder });
  }

  // 무한 스크롤 - 하단 감시 요소가 보이면 다음 페이지를 청한다.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
          void list.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.hasNextPage, list.isFetchingNextPage, rows.length]);

  function toggleAll(checked: boolean) {
    setSelected(checked ? new Set(rows.map((r) => r.indexNo)) : new Set());
  }
  function toggleOne(id: number, checked: boolean) {
    setSelected((s) => {
      const next = new Set(s);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function doMerge() {
    setMerging(true);
    setMergeError(null);
    try {
      const result = await reviewBoardApi.merge([...selected]);
      setMergeOk(`${result.mergedCount}개 리뷰를 병합했습니다. (기준 번호 ${result.targetId})`);
      setSelected(new Set());
      setConfirming(false);
      // 원본의 fetchList(true) - 병합 뒤에는 처음부터 다시 받는다.
      await queryClient.resetQueries({ queryKey });
    } catch (err) {
      setMergeError(err instanceof Error ? err.message : '병합 처리 중 오류가 발생했습니다.');
    } finally {
      setMerging(false);
    }
  }

  const selectedRows = rows.filter((r) => selected.has(r.indexNo));

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">리뷰 관리</h1>
          <p className="page-desc">게시판에 올라온 후기 글입니다. 스크롤을 내리면 더 불러옵니다.</p>
        </div>
      </header>

      <div className="review-panel">
        <div className="review-panel__row">
          <button type="button" className="btn btn--ghost btn--sm" onClick={resetAll}>
            초기화/처음으로
          </button>
          <label className="review-panel__label">제목명 :</label>
          <input
            className="input review-panel__search"
            value={draft.search}
            placeholder="검색어 입력"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyFilters();
              }
            }}
            onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
          />
        </div>

        <div className="review-panel__row">
          <label className="review-panel__label">리뷰 선택 :</label>
          <select
            className="input review-panel__select"
            value={committed.category}
            onChange={(e) => applyFilters({ category: e.target.value as ReviewCategory })}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="review-panel__row">
          <label className="review-panel__label">검색 기간 :</label>
          <input
            className="input"
            type="month"
            value={draft.start}
            onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
          />
          <span className="review-panel__tilde">~</span>
          <input
            className="input"
            type="month"
            value={draft.end}
            onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
          />
          <button type="button" className="btn btn--primary btn--sm" onClick={() => applyFilters()}>
            검색
          </button>
        </div>

        <div className="review-panel__row review-panel__merge-row">
          <button
            type="button"
            className="btn btn--sm review-merge-btn"
            disabled={selected.size < 2}
            onClick={() => setConfirming(true)}
          >
            ✔ 선택 병합
          </button>
          <span className="review-panel__note">
            ※ 2개 이상 선택 시, 가장 작은 번호(오래된 리뷰)에 내용이 통합됩니다.
          </span>
        </div>
      </div>

      <p className="review-info">
        ※ 스크롤을 내리면 데이터를 자동으로 로딩합니다.
        <br />
        〇 컬럼명을 클릭하면 정렬이 됩니다 (예:「조회수」클릭)
      </p>

      {mergeOk && <div className="review-flash review-flash--ok">{mergeOk}</div>}
      {mergeError && <div className="review-flash review-flash--error">{mergeError}</div>}

      {list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <div className="table-wrap review-table-wrap">
          <table className="table review-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    aria-label="현재 목록 전체 선택"
                    title="현재 목록 전체 선택"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={{ width: '60px' }}>번호</th>
                <th style={{ width: '110px' }}>카테고리</th>
                <th style={{ width: '110px' }}>주문번호</th>
                <th style={{ width: '110px' }}>이미지</th>
                <SortableHeader column="subject" label="리뷰제목" committed={committed} onSort={sortBy} />
                <th>작성자</th>
                <SortableHeader column="readcount" label="조회수" committed={committed} onSort={sortBy} width="90px" />
                <SortableHeader column="wdate" label="등록일" committed={committed} onSort={sortBy} width="150px" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.indexNo}
                  className={row.isDuplicate ? `review-dup review-dup--${row.colorGroup % 5}` : undefined}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(row.indexNo)}
                      onChange={(e) => toggleOne(row.indexNo, e.target.checked)}
                    />
                  </td>
                  <td className="td-muted">{i + 1}</td>
                  <td>{row.boardLabel}</td>
                  <td>
                    <button type="button" className="link-btn" onClick={() => openOrder(row.marketIdx)}>
                      {row.marketIdx}
                    </button>
                  </td>
                  <td>
                    {row.thumbnail ? (
                      <button type="button" className="review-thumb-btn" onClick={() => openReview(row.indexNo, row.boardId)}>
                        <img className="review-thumb" src={row.thumbnail} alt="" loading="lazy" />
                      </button>
                    ) : (
                      <span className="review-thumb-empty">✕</span>
                    )}
                  </td>
                  <td className="review-subject">
                    <button type="button" className="link-btn" onClick={() => openReview(row.indexNo, row.boardId)}>
                      {row.subject}
                    </button>
                  </td>
                  <td>
                    <button type="button" className="link-btn" onClick={() => openMember(row.memIdx)}>
                      {row.memName}
                    </button>
                  </td>
                  <td className="td-right">{num(row.readcount)}</td>
                  <td className="td-muted">{row.wdate}</td>
                </tr>
              ))}
              {rows.length === 0 && !list.isFetching && (
                <tr>
                  <td colSpan={9}>
                    <EmptyBox>조건에 맞는 데이터가 없습니다.</EmptyBox>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div ref={sentinelRef} />
          {(list.isFetching || list.isFetchingNextPage) && <Spinner label="데이터를 불러오는 중입니다…" />}
        </div>
      )}

      {confirming && (
        <MergeConfirmModal
          rows={selectedRows}
          pending={merging}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void doMerge()}
        />
      )}
    </section>
  );
}

function SortableHeader({
  column,
  label,
  committed,
  onSort,
  width,
}: {
  column: ReviewOrderBy;
  label: string;
  committed: Filters;
  onSort: (c: ReviewOrderBy) => void;
  width?: string;
}) {
  const active = committed.orderby === column;
  return (
    <th style={width ? { width } : undefined}>
      <button type="button" className="th-sort" onClick={() => onSort(column)}>
        {label}
        {active && (
          <span className="th-sort__arrow th-sort__arrow--on">{committed.order === 'ASC' ? '▲' : '▼'}</span>
        )}
      </button>
    </th>
  );
}

/** 원본 mergeReviews 의 confirm() 을 앱 다른 화면들과 같은 모달 확인으로 바꿨다. */
function MergeConfirmModal({
  rows,
  pending,
  onCancel,
  onConfirm,
}: {
  rows: ReviewBoardRow[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const sorted = [...rows].sort((a, b) => a.indexNo - b.indexNo);
  const target = sorted[0];

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onCancel} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">선택한 {rows.length}개의 리뷰를 병합하시겠습니까?</h2>
        <p className="hint">
          가장 과거의 리뷰(번호 {target?.indexNo})에 내용이 통합되며, 나머지 리뷰의 상품 연결 정보도 함께
          옮겨집니다.
        </p>
        <ul className="review-merge-list">
          {sorted.map((r) => (
            <li key={r.indexNo} className={r.indexNo === target?.indexNo ? 'review-merge-list__target' : undefined}>
              #{r.indexNo} {r.subject}
              {r.indexNo === target?.indexNo && <span className="badge badge--ok">기준</span>}
            </li>
          ))}
        </ul>
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            취소
          </button>
          <button type="button" className="btn btn--primary" disabled={pending} onClick={onConfirm}>
            {pending ? '병합 중…' : '병합하기'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 원본과 같은 팝업 크기·이름으로 연다. */
function openOrder(marketIdx: string) {
  window.open(
    `https://banana.bizhost.kr/adminx/order.php?code=nview&index_no=${marketIdx}`,
    'popup',
    'width=1200,height=800',
  );
}
function openReview(indexNo: number, boardId: string) {
  window.open(
    `https://www.bananamall.co.kr/bbs/read.php?index_no=${indexNo}&page=1&boardid=${boardId}`,
    'popup',
    'width=1200,height=800',
  );
}
function openMember(memIdx: number) {
  window.open(`https://banana.bizhost.kr/adminx/help.php?code=view&index_no=${memIdx}`, 'popup', 'width=1200,height=800');
}
